import { UnreachableError } from 'base/helpers';
import { LibraryCache } from 'base/library_cache';
import { SlotType } from 'base/library_service';
import {
  ElementInstance,
  ElementInstanceState,
  MarginState,
} from 'blocks/editor/model';
import { getCanonicalElementId, SlotInfo } from './slots';

/**
 * This is a separate type to MarginState to make clear the difference between a
 * lack of an override (usually 'undefined' where MarginOverride is optional),
 * and an override that is intended to unset margin (type: 'no-margin').
 */
export type MarginOverride =
  | { type: 'no-margin' }
  | { type: 'top' | 'bottom' | 'vertical'; size: MarginState['size'] };

/**
 * Can be used when applying a margin override to the model.
 */
export const marginOverrideToMarginState = (
  marginOverride: MarginOverride,
): MarginState | undefined =>
  marginOverride.type === 'no-margin'
    ? undefined
    : { direction: marginOverride.type, size: marginOverride.size };

type MarginInfo =
  | { type: 'no-item' }
  | {
      type: 'no-margin' | 'top' | 'bottom' | 'vertical';
      size: MarginState['size'];
    };

const getDefaultMarginInfo = (
  item: ElementInstance | ElementInstanceState | undefined,
  slotType: SlotType,
  slotInfo: SlotInfo,
  library: LibraryCache,
): MarginInfo => {
  if (item === undefined) {
    return { type: 'no-item' };
  }

  const originalItem = slotInfo.originalItems.find(
    (originalItem) =>
      item.source.rootName !== undefined &&
      originalItem.state.source.slug === item.source.slug &&
      originalItem.state.source.rootName === item.source.rootName,
  );

  const canonicalId = getCanonicalElementId(item, library);
  const libraryElement = canonicalId
    ? library.getCachedLibraryElement(canonicalId, {
        fetchIfMissing: true,
      })?.value
    : undefined;

  const defaultMargin =
    originalItem?.state.margin ?? libraryElement?.defaultMargin;

  if (defaultMargin === undefined) {
    return { type: 'no-margin', size: slotType.defaultMarginSize };
  }

  if (
    !(
      defaultMargin.direction === 'top' ||
      defaultMargin.direction === 'bottom' ||
      defaultMargin.direction === 'vertical'
    )
  ) {
    throw new Error(`Unexpected margin direction: ${defaultMargin.direction}`);
  }

  return {
    type: defaultMargin.direction,
    size: defaultMargin.size,
  };
};

const getMarginInfo = (
  item: ElementInstance | ElementInstanceState | undefined,
  slotType: SlotType,
  slotInfo: SlotInfo,
  library: LibraryCache,
): MarginInfo => {
  if (item === undefined) {
    return { type: 'no-item' };
  }

  if (item.margin === undefined) {
    const originalItem = slotInfo.originalItems.find(
      (originalItem) =>
        item.source.rootName !== undefined &&
        originalItem.state.source.slug === item.source.slug &&
        originalItem.state.source.rootName === item.source.rootName,
    );

    const canonicalId = getCanonicalElementId(item, library);
    const libraryElement = canonicalId
      ? library.getCachedLibraryElement(canonicalId, {
          fetchIfMissing: true,
        })?.value
      : undefined;

    return {
      type: 'no-margin',
      size:
        originalItem?.state.margin?.size ??
        libraryElement?.defaultMargin?.size ??
        slotType.defaultMarginSize,
    };
  }

  if (
    !(
      item.margin.direction === 'top' ||
      item.margin.direction === 'bottom' ||
      item.margin.direction === 'vertical'
    )
  ) {
    throw new Error(`Unexpected margin direction: ${item.margin.direction}`);
  }

  return {
    type: item.margin.direction,
    size: item.margin.size,
  };
};

export const computeNewLocationMarginOverrides = ({
  slotItems,
  slotInfo,
  library,
  index,
}: {
  slotItems: ReadonlyArray<ElementInstance | ElementInstanceState>;
  slotInfo: SlotInfo;
  library: LibraryCache;
  index: number;
}): ReadonlyArray<{ marginOverride: MarginOverride; index: number }> => {
  const slotType = library.getCachedSlotType(slotInfo.typeId);
  if (slotType?.value?.itemMargins !== 'vertical') return [];

  const currentDefault = getDefaultMarginInfo(
    slotItems[index],
    slotType.value,
    slotInfo,
    library,
  );
  const current = getMarginInfo(
    slotItems[index],
    slotType.value,
    slotInfo,
    library,
  );

  const newCurrentSize =
    current.type === 'no-item'
      ? currentDefault.type === 'no-item'
        ? slotType.value.defaultMarginSize
        : currentDefault.size
      : current.size;

  // We'll apply these changes when we don't intend to change the current item's
  // margin. Normally that'll be a no-op, but in cases where we're adding a new
  // item, we'll set the item's default margin.
  const noChangesToCurrent =
    currentDefault.type !== 'no-item'
      ? [
          {
            index,
            marginOverride: currentDefault,
          },
        ]
      : [];

  const aboveIndex = index - 1;
  const above = getMarginInfo(
    slotItems[aboveIndex],
    slotType.value,
    slotInfo,
    library,
  );

  const belowIndex = index + 1;
  const below = getMarginInfo(
    slotItems[belowIndex],
    slotType.value,
    slotInfo,
    library,
  );

  const logUnexpectedState = () => {
    console.warn(
      "Unexpected state when computing new location's margin overrides",
      {
        below,
        current,
        above,
      },
    );
  };

  switch (currentDefault.type) {
    case 'bottom':
      switch (above.type) {
        case 'bottom':
        case 'vertical':
          switch (below.type) {
            case 'bottom':
            case 'no-margin':
              return noChangesToCurrent;
            case 'vertical':
            case 'top':
            case 'no-item':
              logUnexpectedState();
              return [];
            default:
              throw new UnreachableError(below);
          }
        case 'top':
          switch (below.type) {
            case 'bottom':
            case 'no-margin':
              // Change element above to margin-vertical.
              return [
                {
                  index: aboveIndex,
                  marginOverride: { type: 'vertical', size: above.size },
                },
              ];
            case 'vertical':
            case 'top':
            case 'no-item':
              // Change element above to margin-vertical, unwrap current element.
              return [
                {
                  index: aboveIndex,
                  marginOverride: { type: 'vertical', size: above.size },
                },
                { index, marginOverride: { type: 'no-margin' } },
              ];
            default:
              throw new UnreachableError(below);
          }
        case 'no-margin':
          switch (below.type) {
            case 'bottom':
              // Wrap element above in margin-bottom.
              return [
                {
                  index: aboveIndex,
                  marginOverride: { type: 'bottom', size: above.size },
                },
              ];
            case 'vertical':
            case 'top':
            case 'no-item':
              // Wrap element above in margin-bottom, unwrap current element.
              return [
                {
                  index: aboveIndex,
                  marginOverride: { type: 'bottom', size: above.size },
                },
                { index, marginOverride: { type: 'no-margin' } },
              ];
            case 'no-margin':
              // Unwrap current element.
              return [{ index, marginOverride: { type: 'no-margin' } }];
            default:
              throw new UnreachableError(below);
          }
        case 'no-item':
          switch (below.type) {
            case 'bottom':
            case 'no-margin':
              return noChangesToCurrent;
            case 'vertical':
            case 'top':
            case 'no-item':
              // Unwrap current element.
              return [{ index, marginOverride: { type: 'no-margin' } }];
            default:
              throw new UnreachableError(below);
          }
        default:
          throw new UnreachableError(above);
      }

    case 'vertical':
      switch (above.type) {
        case 'bottom':
          switch (below.type) {
            case 'bottom':
            case 'no-margin':
              // Unwrap item above.
              return [
                { index: aboveIndex, marginOverride: { type: 'no-margin' } },
              ];
            case 'vertical':
            case 'top':
            case 'no-item':
              logUnexpectedState();
              return noChangesToCurrent;
            default:
              throw new UnreachableError(below);
          }
        case 'vertical':
          switch (below.type) {
            case 'bottom':
            case 'no-margin':
              // Change current element to margin-bottom.
              return [
                {
                  index,
                  marginOverride: { type: 'bottom', size: newCurrentSize },
                },
              ];
            case 'vertical':
            case 'top':
            case 'no-item':
              logUnexpectedState();
              return [];
            default:
              throw new UnreachableError(below);
          }
        case 'top':
          switch (below.type) {
            case 'bottom':
            case 'no-margin':
              return noChangesToCurrent;
            case 'vertical':
            case 'top':
              // Change element below to margin-bottom.
              return [
                {
                  index: belowIndex,
                  marginOverride: { type: 'bottom', size: below.size },
                },
              ];
            case 'no-item':
              // Change element above to margin-vertical, unwrap current element.
              return [
                {
                  index: aboveIndex,
                  marginOverride: { type: 'vertical', size: above.size },
                },
                { index, marginOverride: { type: 'no-margin' } },
              ];
            default:
              throw new UnreachableError(below);
          }
        case 'no-margin':
          switch (below.type) {
            case 'bottom':
            case 'no-margin':
              return noChangesToCurrent;
            case 'vertical':
              // Change element below to margin-bottom.
              return [
                {
                  index: belowIndex,
                  marginOverride: { type: 'bottom', size: below.size },
                },
              ];
            case 'top':
              // Unwrap element below.
              return [
                { index: belowIndex, marginOverride: { type: 'no-margin' } },
              ];
            case 'no-item':
              // Change current element to margin-top.
              return [
                {
                  index,
                  marginOverride: { type: 'top', size: newCurrentSize },
                },
              ];
            default:
              throw new UnreachableError(below);
          }
        case 'no-item':
          switch (below.type) {
            case 'bottom':
            case 'no-margin':
              // Change current element to margin-bottom.
              return [
                {
                  index,
                  marginOverride: { type: 'bottom', size: newCurrentSize },
                },
              ];
            case 'vertical':
            case 'top':
              logUnexpectedState();
              return [];
            case 'no-item':
              // Unwrap current element.
              return [{ index, marginOverride: { type: 'no-margin' } }];
            default:
              throw new UnreachableError(below);
          }
        default:
          throw new UnreachableError(above);
      }

    case 'top':
      switch (above.type) {
        case 'bottom':
          switch (below.type) {
            case 'bottom':
            case 'no-margin':
              // Unwrap element above, change current element to margin-vertical.
              return [
                { index: aboveIndex, marginOverride: { type: 'no-margin' } },
                {
                  index,
                  marginOverride: { type: 'vertical', size: newCurrentSize },
                },
              ];
            case 'vertical':
            case 'top':
            case 'no-item':
              logUnexpectedState();
              return [];
            default:
              throw new UnreachableError(below);
          }
        case 'vertical':
          switch (below.type) {
            case 'bottom':
            case 'no-margin':
              // Change current element to margin-bottom.
              return [
                {
                  index,
                  marginOverride: { type: 'bottom', size: newCurrentSize },
                },
              ];
            case 'vertical':
            case 'top':
            case 'no-item':
              logUnexpectedState();
              return [];
            default:
              throw new UnreachableError(below);
          }
        case 'top':
          switch (below.type) {
            case 'bottom':
            case 'no-margin':
              // Change current element to margin-vertical.
              return [
                {
                  index,
                  marginOverride: { type: 'vertical', size: newCurrentSize },
                },
              ];
            case 'vertical':
            case 'top':
              return noChangesToCurrent;
            case 'no-item':
              // Change element above to margin-vertical, unwrap current element.
              return [
                {
                  index: aboveIndex,
                  marginOverride: { type: 'vertical', size: above.size },
                },
                { index, marginOverride: { type: 'no-margin' } },
              ];
            default:
              throw new UnreachableError(below);
          }
        case 'no-margin':
          switch (below.type) {
            case 'bottom':
            case 'no-margin':
              // Change current element to margin-vertical.
              return [
                {
                  index,
                  marginOverride: { type: 'vertical', size: newCurrentSize },
                },
              ];
            case 'vertical':
              // Change current element to margin-vertical, change element below to margin-bottom.
              return [
                {
                  index,
                  marginOverride: { type: 'vertical', size: newCurrentSize },
                },
                {
                  index: belowIndex,
                  marginOverride: { type: 'bottom', size: below.size },
                },
              ];
            case 'top':
              // Change current element to margin-vertical, unwrap element below.
              return [
                {
                  index,
                  marginOverride: { type: 'vertical', size: newCurrentSize },
                },
                { index: belowIndex, marginOverride: { type: 'no-margin' } },
              ];
            case 'no-item':
              // Change current element to margin-top.
              return [
                {
                  index,
                  marginOverride: { type: 'top', size: newCurrentSize },
                },
              ];
            default:
              throw new UnreachableError(below);
          }
        case 'no-item':
          switch (below.type) {
            case 'bottom':
            case 'no-margin':
              // Change current element to margin-bottom.
              return [
                {
                  index,
                  marginOverride: { type: 'bottom', size: newCurrentSize },
                },
              ];
            case 'no-item':
              // Unwrap current element.
              return [{ index, marginOverride: { type: 'no-margin' } }];
            case 'vertical':
            case 'top':
              logUnexpectedState();
              return [];
            default:
              throw new UnreachableError(below);
          }
        default:
          throw new UnreachableError(above);
      }

    case 'no-margin':
      switch (above.type) {
        case 'bottom':
          switch (below.type) {
            case 'bottom':
            case 'no-margin':
              // Wrap current element in margin-bottom.
              return [
                {
                  index,
                  marginOverride: { type: 'bottom', size: newCurrentSize },
                },
              ];
            case 'vertical':
            case 'top':
            case 'no-item':
              logUnexpectedState();
              return [];
            default:
              throw new UnreachableError(below);
          }
        case 'vertical':
          switch (below.type) {
            case 'bottom':
            case 'no-margin':
              // Wrap current element in margin-bottom.
              return [
                {
                  index,
                  marginOverride: { type: 'bottom', size: newCurrentSize },
                },
              ];
            case 'vertical':
            case 'top':
            case 'no-item':
              logUnexpectedState();
              return [];
            default:
              throw new UnreachableError(below);
          }
        case 'top':
          switch (below.type) {
            case 'bottom':
            case 'no-margin':
              // Change element above to margin-vertical, wrap current element in margin-bottom.
              return [
                {
                  index: aboveIndex,
                  marginOverride: { type: 'vertical', size: above.size },
                },
                {
                  index,
                  marginOverride: { type: 'bottom', size: newCurrentSize },
                },
              ];
            case 'vertical':
            case 'top':
            case 'no-item':
              // Change element above to margin-vertical.
              return [
                {
                  index: aboveIndex,
                  marginOverride: { type: 'vertical', size: above.size },
                },
              ];
            default:
              throw new UnreachableError(below);
          }
        case 'no-margin':
          switch (below.type) {
            case 'bottom':
              // Wrap item above in margin-bottom, wrap current element in margin-bottom.
              return [
                {
                  index: aboveIndex,
                  marginOverride: { type: 'bottom', size: above.size },
                },
                {
                  index,
                  marginOverride: { type: 'bottom', size: newCurrentSize },
                },
              ];
            case 'vertical':
            case 'top':
            case 'no-item':
              // Wrap item above in margin-bottom.
              return [
                {
                  index: aboveIndex,
                  marginOverride: { type: 'bottom', size: above.size },
                },
              ];
            case 'no-margin':
              return noChangesToCurrent;
            default:
              throw new UnreachableError(below);
          }
        case 'no-item':
          switch (below.type) {
            case 'bottom':
            case 'no-margin':
              // Wrap current element in margin-bottom.
              return [
                {
                  index,
                  marginOverride: { type: 'bottom', size: newCurrentSize },
                },
              ];
            case 'no-item':
              return noChangesToCurrent;
            case 'vertical':
            case 'top':
              logUnexpectedState();
              return [];
            default:
              throw new UnreachableError(below);
          }
        default:
          throw new UnreachableError(above);
      }

    case 'no-item':
      throw new Error('Unexpected no-item');

    default:
      throw new UnreachableError(currentDefault);
  }
};

export const computeOldLocationMarginOverrides = ({
  slotItems,
  slotInfo,
  library,
  index,
}: {
  slotItems: ReadonlyArray<ElementInstance | ElementInstanceState>;
  slotInfo: SlotInfo;
  library: LibraryCache;
  index: number;
}): ReadonlyArray<{ marginOverride: MarginOverride; index: number }> => {
  const slotType = library.getCachedSlotType(slotInfo.typeId);
  if (slotType?.value?.itemMargins !== 'vertical') return [];

  const current = getMarginInfo(
    slotItems[index],
    slotType.value,
    slotInfo,
    library,
  );

  const aboveIndex = index - 1;
  const above = getMarginInfo(
    slotItems[aboveIndex],
    slotType.value,
    slotInfo,
    library,
  );

  const belowIndex = index + 1;
  const below = getMarginInfo(
    slotItems[belowIndex],
    slotType.value,
    slotInfo,
    library,
  );

  const logUnexpectedState = () => {
    console.warn(
      "Unexpected state when computing old location's margin overrides",
      {
        below,
        current,
        above,
      },
    );
  };

  switch (current.type) {
    case 'bottom':
      switch (above.type) {
        case 'bottom':
        case 'vertical':
        case 'no-item':
          switch (below.type) {
            case 'bottom':
            case 'no-margin':
              return [];
            case 'vertical':
            case 'top':
            case 'no-item':
              logUnexpectedState();
              return [];
            default:
              throw new UnreachableError(below);
          }
        case 'top':
          switch (below.type) {
            case 'bottom':
            case 'no-margin':
              // Change element above to margin-vertical.
              return [
                {
                  index: aboveIndex,
                  marginOverride: { type: 'vertical', size: above.size },
                },
              ];
            case 'vertical':
            case 'top':
            case 'no-item':
              logUnexpectedState();
              return [];
            default:
              throw new UnreachableError(below);
          }
        case 'no-margin':
          switch (below.type) {
            case 'bottom':
            case 'no-margin':
              // Wrap element above in margin-bottom.
              return [
                {
                  index: aboveIndex,
                  marginOverride: { type: 'bottom', size: above.size },
                },
              ];
            case 'vertical':
            case 'top':
            case 'no-item':
              logUnexpectedState();
              return [];
            default:
              throw new UnreachableError(below);
          }
        default:
          throw new UnreachableError(above);
      }

    case 'vertical':
      switch (above.type) {
        case 'bottom':
        case 'vertical':
        case 'no-item':
          logUnexpectedState();
          return [];
        case 'top':
          switch (below.type) {
            case 'bottom':
            case 'no-margin':
              // Change element above to margin-vertical.
              return [
                {
                  index: aboveIndex,
                  marginOverride: { type: 'vertical', size: above.size },
                },
              ];
            case 'vertical':
            case 'top':
            case 'no-item':
              logUnexpectedState();
              return [];
            default:
              throw new UnreachableError(below);
          }
        case 'no-margin':
          switch (below.type) {
            case 'bottom':
            case 'no-margin':
              // Wrap element above in margin-bottom.
              return [
                {
                  index: aboveIndex,
                  marginOverride: { type: 'bottom', size: above.size },
                },
              ];
            case 'vertical':
            case 'top':
            case 'no-item':
              logUnexpectedState();
              return [];
            default:
              throw new UnreachableError(below);
          }
        default:
          throw new UnreachableError(above);
      }

    case 'top':
      switch (above.type) {
        case 'bottom':
        case 'vertical':
        case 'no-item':
          logUnexpectedState();
          return [];
        case 'top':
          switch (below.type) {
            case 'bottom':
            case 'no-margin':
              // Change element above to margin-vertical.
              return [
                {
                  index: aboveIndex,
                  marginOverride: { type: 'vertical', size: above.size },
                },
              ];
            case 'vertical':
            case 'top':
            case 'no-item':
              return [];
            default:
              throw new UnreachableError(below);
          }
        case 'no-margin':
          switch (below.type) {
            case 'no-item':
              return [];
            case 'bottom':
            case 'vertical':
            case 'top':
            case 'no-margin':
              logUnexpectedState();
              return [];
            default:
              throw new UnreachableError(below);
          }
        default:
          throw new UnreachableError(above);
      }

    case 'no-margin':
      switch (above.type) {
        case 'bottom':
          switch (below.type) {
            case 'bottom':
            case 'no-margin':
              return [];
            case 'vertical':
            case 'top':
            case 'no-item':
              // Unwrap element above.
              return [
                { index: aboveIndex, marginOverride: { type: 'no-margin' } },
              ];
            default:
              throw new UnreachableError(below);
          }
        case 'vertical':
          switch (below.type) {
            case 'bottom':
            case 'no-margin':
              return [];
            case 'vertical':
              // Change element below to margin-bottom.
              return [
                {
                  index: belowIndex,
                  marginOverride: { type: 'bottom', size: below.size },
                },
              ];
            case 'top':
              // Unwrap element below.
              return [
                { index: belowIndex, marginOverride: { type: 'no-margin' } },
              ];
            case 'no-item':
              // Change element above to margin-top.
              return [
                {
                  index: aboveIndex,
                  marginOverride: { type: 'top', size: above.size },
                },
              ];
            default:
              throw new UnreachableError(below);
          }
        case 'top':
          switch (below.type) {
            case 'bottom':
            case 'no-margin':
              // Change element above to margin-vertical.
              return [
                {
                  index: aboveIndex,
                  marginOverride: { type: 'vertical', size: above.size },
                },
              ];
            case 'vertical':
            case 'top':
            case 'no-item':
              return [];
            default:
              throw new UnreachableError(below);
          }
        case 'no-margin':
          switch (below.type) {
            case 'bottom':
            case 'vertical':
            case 'top':
            case 'no-margin':
              return [];
            case 'no-item':
              logUnexpectedState();
              return [];
            default:
              throw new UnreachableError(below);
          }
        case 'no-item':
          switch (below.type) {
            case 'bottom':
            case 'no-margin':
              return [];
            case 'top':
            case 'no-item':
              logUnexpectedState();
              return [];
            case 'vertical':
              // Change element below to margin-bottom.
              return [
                {
                  index: belowIndex,
                  marginOverride: { type: 'bottom', size: below.size },
                },
              ];
            default:
              throw new UnreachableError(below);
          }
        default:
          throw new UnreachableError(above);
      }

    case 'no-item':
      throw new Error('Unexpected no-item');

    default:
      throw new UnreachableError(current);
  }
};
